import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState,
} from 'react'

import type { IconProps } from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'
import type { Currency } from '@fv/models'

import { CurrencySelector } from '@/features/quote/CurrencySelector'
import { AddRateButton } from '@/features/quote/ManualRatePanel'
import { PrintRatesButton } from '@/features/quote/PrintRatesButton'
import type { QuoteRequestResponses } from '@/hooks/shipments/useQuoteRequestResponses'
import { useExchangeRatesForLoad } from '@/hooks/useExchangeRate'
import arrayOfLength from '@/utils/arrayOfLength'

import { DividedContent } from './DividedContent'
import InfoBox from './InfoBox'
import { Pinline } from './Pinline'
import { QuoteListFilter } from './QuoteRequest/QuoteListFilter'
import type { ListState, ListStatus } from './QuoteRequest/QuoteRequestDetail'
import ResponseRow from './ResponseRow'
import TruckSelector from './shared/TruckSelector'

const emptyStates = {
  active: 'Nobody has responded yet.',
  declined: 'Nobody has declined.',
  pending: 'No pending responses.',
}

type ResponseListFilter = {
  icon: IconProps['icon']
  label: string
  name: ListStatus
}

const filters: ResponseListFilter[] = [
  {
    icon: 'comment-alt-lines',
    label: 'Responses',
    name: 'active',
  },
  {
    icon: 'comment-alt-minus',
    label: 'Yet to respond',
    name: 'pending',
  },
  {
    icon: 'comment-alt-slash',
    label: 'Declined',
    name: 'declined',
  },
]

type ResponseListProps = {
  filter: ListStatus
  listState: ListState
  loadId: string
  loads: FullShipment[]
  onMessageClick: () => void
  onRowClick: () => void
  responses: QuoteRequestResponses
  selectTruck: (i: number) => void
  setFilter: Dispatch<SetStateAction<ListStatus>>
  setListState: Dispatch<SetStateAction<ListState>>
}

export const ResponseList = ({
  filter,
  listState,
  loadId,
  loads,
  onMessageClick,
  onRowClick,
  responses,
  selectTruck,
  setFilter,
  setListState,
}: ResponseListProps) => {
  const [displayCurrency, setDisplayCurrency] = useState<Currency | null>(null)
  const loadIndex = loads.findIndex(load => load.loadId === loadId)
  const activeLoad = loads[loadIndex]
  const hasNonUsdRates = responses.active.some(
    r => r.currency && r.currency !== 'usd',
  )
  const exchangeRates = useExchangeRatesForLoad(activeLoad, hasNonUsdRates)

  const setFilterId = useCallback(
    (id: string) => {
      setListState(prev => ({
        ...prev,
        [loadId]: {
          ...prev[loadId],
          [filter]: id,
        },
      }))
    },
    [filter, loadId, setListState],
  )

  const toggleSelectedId = useCallback(
    (id: string) => {
      setListState(prev => ({
        ...prev,
        [loadId]: {
          ...prev[loadId],
          [filter]: id,
          selected: prev[loadId].selected === id ? '' : id,
        },
      }))
    },
    [filter, loadId, setListState],
  )

  function onManualRateError(loadId: string) {
    setListState(prev => ({
      ...prev,
      [loadId]: {
        ...prev[loadId],
        active: '',
      },
    }))
  }

  function onManualRateSuccess(loadId: string, quoteId: string) {
    setListState(prev => ({
      ...prev,
      [loadId]: {
        ...prev[loadId],
        active: quoteId,
      },
    }))
  }

  if (!loads.length) {
    return (
      <div className="p-6">
        <InfoBox icon="spinner" className="mb-0">
          Loading...
        </InfoBox>
      </div>
    )
  }

  return (
    <div className="ltl-rates-ctn relative flex w-full flex-col overflow-auto bg-fv-gray-50 scrollbar-gutter-stable">
      <div className="b1200:pt-6 flex items-center px-6 py-8">
        {loads.length > 1 && (
          <div className="flex items-center gap-x-1 mr-4">
            <span className="mr-1">Truck</span>

            {arrayOfLength(loads.length).map(i => (
              <TruckSelector
                active={i === loadIndex}
                key={i}
                selectTruck={() => selectTruck(i)}
                truckNumber={i + 1}
              />
            ))}
          </div>
        )}

        <DividedContent>
          {filters.map(({ icon, label, name }, index) => (
            <QuoteListFilter
              active={filter === name}
              icon={icon}
              key={index}
              label={label}
              onClick={() => setFilter(name)}
            />
          ))}
        </DividedContent>
        <Pinline className="flex-1 mx-2" dashed />
        <DividedContent>
          <AddRateButton
            icon="pen-alt"
            load={activeLoad}
            onError={onManualRateError}
            onSuccess={onManualRateSuccess}
          >
            Manually enter rate
          </AddRateButton>
          <PrintRatesButton loadId={loadId}>Print rates</PrintRatesButton>
        </DividedContent>

        {hasNonUsdRates && (
          <>
            <Pinline className="flex-1 mx-4 !border-fv-blue" dotted />
            <CurrencySelector
              size="small"
              className="w-auto"
              value={displayCurrency}
              onChange={setDisplayCurrency}
              locations={activeLoad?.locations}
            />
          </>
        )}
      </div>

      <div data-cy="rates-table" className="flex-1 overflow-auto px-6">
        {responses[filter].length < 1 ? (
          <InfoBox icon="ban" className="mb-0">
            {emptyStates[filter]}
          </InfoBox>
        ) : (
          responses[filter].map(data => (
            <ResponseRow
              data={data}
              displayCurrency={displayCurrency}
              equipmentType={activeLoad.equipment.type}
              exchangeRates={exchangeRates}
              filter={filter}
              handleChange={() => toggleSelectedId(data.id)}
              handleMessagesClick={onMessageClick}
              handleRowClick={() => {
                setFilterId(data.id)
                onRowClick()
              }}
              isActive={data.id === listState[loadId]?.[filter]}
              isAwarded={activeLoad.selectedQuote?._id === data.id}
              isDisabled={Boolean(activeLoad.selectedQuote)}
              isSelected={data.id === listState[loadId]?.selected}
              key={data.id}
              load={activeLoad}
            />
          ))
        )}
      </div>
    </div>
  )
}
